import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatINR, discountPct, track } from "../lib/api";
import { useStore } from "../lib/store";
import { useSEO } from "../lib/seo";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";
import { Star, Heart, ShoppingBag, Zap, Minus, Plus, Check, ChevronDown, MapPin, RotateCcw, Package } from "lucide-react";

function PinCheck() {
  const [pin, setPin] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const check = async () => {
    setError(""); setResult(null);
    try {
      const r = await api.post("/shipping/pin-check", { pin });
      setResult(r.data);
      track("delivery_check", { pin_prefix: pin.slice(0, 3) });
    } catch (e) { setError(e.response?.data?.detail || "Please check the PIN code and try again."); }
  };
  return (
    <div className="bg-white rounded-2xl card-outline p-4 mt-4" data-testid="pin-check-card">
      <label htmlFor="pin-input" className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Delivery check</label>
      <div className="flex gap-2 mt-2">
        <input id="pin-input" inputMode="numeric" maxLength={6} value={pin} data-testid="pin-input"
          onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setResult(null); }}
          onKeyDown={(e) => e.key === "Enter" && pin.length === 6 && check()}
          placeholder="Enter 6-digit PIN" className="flex-1 min-w-0 border border-charcoal/15 rounded-full px-4 py-2.5 text-sm" />
        <button onClick={check} disabled={pin.length !== 6} data-testid="pin-check-button"
          className="bg-forest text-white text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-40">Check</button>
      </div>
      {error && <p role="alert" className="text-xs text-coral mt-2" data-testid="pin-error">{error}</p>}
      {result && (
        <div className="text-xs mt-2 leading-relaxed" data-testid="pin-result">
          {result.serviceable ? (
            <p className="text-forest font-semibold"><Check className="w-3.5 h-3.5 inline" /> {result.note}</p>
          ) : (
            <p className="text-coral font-semibold" data-testid="pin-unsupported">{result.message}</p>
          )}
          {result.serviceable && !result.cod && <p className="text-charcoal/60 mt-1">COD isn't available here — prepaid delivery is available.</p>}
        </div>
      )}
    </div>
  );
}

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, pushRecent, recent, toggleWishlist, inWishlist, track, settings } = useStore();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [variantId, setVariantId] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const swipeX = useRef(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [reviewForm, setReviewForm] = useState({ open: false, name: "", rating: 5, title: "", body: "", done: false, error: "" });
  const galleryRef = useRef(null);

  useEffect(() => {
    setP(null); setNotFound(false); setVariantId(null); setQty(1); setActiveImg(0);
    api.get(`/products/${slug}`)
      .then((r) => {
        setP(r.data);
        setVariantId(r.data.variants[0]?.id);
        pushRecent(r.data);
        track("view_item", { item_id: slug, price: r.data.price, currency: "INR" });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: p ? `${p.name} — Haven Hives` : "Product — Haven Hives",
    description: p?.description,
    jsonLd: p ? {
      "@context": "https://schema.org", "@type": "Product", name: p.name,
      image: p.images.map((i) => i.url), description: p.description,
      offers: { "@type": "Offer", priceCurrency: "INR", price: p.price, availability: p.variants.some((v) => v.stock > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
      ...(p.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: p.rating.avg, reviewCount: p.rating.count } } : {}),
    } : null,
  });

  if (notFound) {
    return (
      <div className="max-w-xl mx-auto text-center py-24 px-5" data-testid="product-not-found">
        <h1 className="font-display text-3xl font-semibold">This little joy has flown away</h1>
        <p className="text-charcoal/70 mt-3">The product you're looking for isn't available. It may have sold out or moved.</p>
        <Link to="/collections/shop-all" className="btn-primary mt-6">Browse the shop</Link>
      </div>
    );
  }
  if (!p) return <div className="mx-auto max-w-[1700px] px-5 py-16" aria-busy="true"><div className="h-96 rounded-2xl bg-surface animate-pulse" /></div>;

  const variant = p.variants.find((v) => v.id === variantId) || p.variants[0];
  const vPrice = variant?.price ?? p.price;
  const vMrp = variant?.mrp ?? p.mrp;
  const stock = variant?.stock ?? 0;
  const off = discountPct(vPrice, vMrp);
  const wished = inWishlist(p.slug);
  const isDemoReview = p.reviews.some((r) => r.demo);

  const add = (silent) => {
    if (stock <= 0) return;
    addToCart({ ...p, slug: p.slug }, variant, qty, { silent });
  };
  const buyNow = () => { add(true); track("begin_checkout", { value: vPrice * qty, currency: "INR" }); navigate("/checkout"); };

  return (
    <div className="pb-24 lg:pb-0" data-testid="product-page">
      <div className="mx-auto max-w-[1700px] px-5 sm:px-8 xl:px-14 pt-6">
        <nav aria-label="Breadcrumb" className="text-sm text-subtext flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-charcoal">Home</Link> <span aria-hidden="true" className="text-line">|</span>{" "}
          <Link to={`/collections/${p.category === "bottles" ? "water-bottles" : p.category === "lunchboxes" ? "lunchboxes" : p.category === "stationery" ? "stationery" : p.category === "gifts" ? "gifts" : "luggage"}`} className="hover:text-charcoal capitalize">{p.category}</Link>{" "}
          <span aria-hidden="true" className="text-line">|</span> <span className="text-charcoal">{p.name}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 mt-6">
          <div className="lg:col-span-7 flex flex-col-reverse sm:flex-row gap-3">
            {p.images.length > 1 && (
              <div className="flex sm:flex-col gap-3 overflow-x-auto no-scrollbar shrink-0" role="tablist" aria-label="Product images">
                {p.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} role="tab" aria-selected={activeImg === i} aria-label={`View image ${i + 1}`}
                    className={`w-[72px] h-[72px] rounded-xl overflow-hidden bg-surface shrink-0 border transition-colors ${activeImg === i ? "border-charcoal" : "border-transparent"}`}>
                    <img src={img.url} alt="" width="72" height="72" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
            <div className="relative rounded-2xl overflow-hidden bg-surface flex-1" data-testid="product-gallery">
              <div className="aspect-square overflow-hidden touch-pan-y"
                onTouchStart={(e) => { swipeX.current = e.touches[0].clientX; }}
                onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - swipeX.current; if (Math.abs(dx) > 40 && p.images.length > 1) setActiveImg((activeImg + (dx < 0 ? 1 : p.images.length - 1)) % p.images.length); }}
                onMouseEnter={() => setZoom(true)} onMouseLeave={() => setZoom(false)}
                onMouseMove={(e) => { const t = e.currentTarget; t.style.transformOrigin = `${(e.nativeEvent.offsetX / t.offsetWidth) * 100}% ${(e.nativeEvent.offsetY / t.offsetHeight) * 100}%`; }}>
                <img src={p.images[activeImg]?.url} alt={p.images[activeImg]?.alt || p.name} width="800" height="800" fetchPriority="high"
                  className={`w-full h-full object-cover transition-transform duration-300 ${zoom ? "scale-[1.6]" : "scale-100"}`} data-testid="product-main-image" />
              </div>
              {p.images.length > 1 && (
                <div className="sm:hidden absolute bottom-3 inset-x-0 flex justify-center gap-1.5" aria-hidden="true">
                  {p.images.map((_, i) => <span key={i} data-testid={`gallery-dot-${i}`} aria-current={i === activeImg} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeImg ? "w-5 bg-charcoal" : "w-1.5 bg-white/80"}`} />)}
                </div>
              )}
              {p.tags?.includes("new") ? <span className="absolute top-4 left-4 bg-mint text-white text-[11px] px-2.5 py-1.5 rounded-full leading-none">New</span>
                : off > 0 && <span className="absolute top-4 left-4 bg-coral text-white text-[11px] px-2.5 py-1.5 rounded-full leading-none">Sale</span>}
            </div>
          </div>

          <div className="lg:col-span-5">
            {p.rating && (
              <span className="inline-flex items-center gap-1 text-sm" data-testid="product-rating">
                <Star className="w-4 h-4 fill-charcoal text-charcoal" /> {p.rating.avg} · {p.rating.count} reviews
                {isDemoReview && <span className="text-xs font-normal text-charcoal/50">(sample reviews)</span>}
              </span>
            )}
            <h1 className="font-display text-4xl sm:text-[2.75rem] leading-tight mt-2" data-testid="product-title">{p.name}</h1>
            <p className="text-sm text-subtext mt-3">Category: <Link to={`/collections/${p.category === "bottles" ? "water-bottles" : p.category}`} className="text-charcoal underline underline-offset-2 capitalize">{p.category}</Link></p>

            <div className="mt-5 flex items-center gap-3 flex-wrap" data-testid="product-price-block">
              <span className={`font-body text-3xl ${vMrp && vMrp > vPrice ? "text-coral" : ""}`} data-testid="pdp-price">{formatINR(vPrice)}</span>
              {vMrp && vMrp > vPrice && <span className="text-lg text-subtext/70 line-through">{formatINR(vMrp)}</span>}
              <span className="text-xs text-subtext w-full">{p.tax_wording || "Prices include GST."} <Link to="/policies/shipping" className="underline">Shipping</Link> calculated at checkout.</span>
            </div>

            <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
              {p.benefits.slice(0, 3).map((b, i) => (
                <li key={i} className="flex gap-2 text-sm leading-relaxed" data-testid={`benefit-${i}`}>
                  <Check className="w-4 h-4 shrink-0 mt-0.5" /> {b}
                </li>
              ))}
            </ul>

            {p.variants.length > 1 && (
              <div className="mt-6" role="radiogroup" aria-label="Choose a colour">
                <p className="text-sm" data-testid="variant-label">Colour: <span className="text-subtext">{variant?.name}</span></p>
                <div className="flex gap-2.5 mt-2.5 flex-wrap">
                  {p.variants.map((v) => (
                    <button key={v.id} role="radio" aria-checked={v.id === variantId} onClick={() => { setVariantId(v.id); setQty(1); track("select_variant", { item_id: p.slug, variant_id: v.id }); }}
                      data-testid={`variant-${v.name}`} disabled={v.stock === 0}
                      className={`flex items-center gap-2 border rounded-full pl-1.5 pr-4 h-11 text-sm transition-colors ${v.id === variantId ? "bg-forest text-white border-forest" : "bg-white border-line hover:border-charcoal"} ${v.stock === 0 ? "opacity-40" : ""}`}>
                      <span className="w-7 h-7 rounded-full border border-white/40" style={{ background: v.swatch }} aria-hidden="true" />
                      {v.name}{v.stock === 0 ? " (sold out)" : ""}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <p className="text-sm" data-testid="stock-status">
                {stock > 5 ? <span className="text-[#0D8756]">In stock — ready to ship</span> : stock > 0 ? <span className="text-[#0D8756]">Hurry up, only {stock} items left in stock.</span> : <span className="text-subtext">Out of stock</span>}
              </p>
              <div className="h-1 rounded-full bg-line mt-2.5 overflow-hidden"><span className="block h-full bg-[#0D8756] rounded-full transition-all duration-700" style={{ width: `${Math.min(100, Math.max(6, (stock / 25) * 100))}%` }} /></div>
            </div>

            <div className="mt-5 flex flex-row items-center gap-2 sm:gap-3">
              <div className="flex items-center justify-between border border-line rounded-full px-2 h-12 bg-white w-28 sm:w-32 shrink-0 self-center">
                <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" data-testid="qty-minus" className="w-10 h-10 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                <span className="w-8 text-center" data-testid="qty-value" aria-live="polite">{qty}</span>
                <button onClick={() => setQty(Math.min(10, qty + 1))} aria-label="Increase quantity" data-testid="qty-plus" className="w-10 h-10 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
              </div>
              {stock > 0 ? (
                <button onClick={() => add()} data-testid="add-to-cart-btn" className="btn-secondary flex-1">
                  <ShoppingBag className="w-5 h-5" /> Add To Cart
                </button>
              ) : (
                <div className="flex-1 bg-surface rounded-2xl p-4 text-center">
                  <p className="text-sm">Out of stock right now</p>
                  <a href={`mailto:hello@havenhives.in?subject=Notify me — ${p.name}`} className="mt-2 inline-block text-sm underline underline-offset-4" data-testid="back-in-stock-link">Email me when it's back</a>
                </div>
              )}
              <button onClick={() => toggleWishlist({ slug: p.slug, name: p.name, price: p.price, mrp: p.mrp, image: p.images[0]?.url, category: p.category })}
                aria-pressed={wished} aria-label={wished ? "Remove from wishlist" : "Add to wishlist"} data-testid="pdp-wishlist-btn"
                className={`w-12 h-12 rounded-full border border-line bg-white hover:bg-surface transition-colors flex items-center justify-center shrink-0 ${wished ? "text-coral" : ""}`}>
                <Heart className="w-5 h-5" fill={wished ? "currentColor" : "none"} />
              </button>
            </div>
            {stock > 0 && <button onClick={buyNow} data-testid="buy-now-btn" className="btn-primary w-full mt-3"><Zap className="w-5 h-5" /> Buy It Now</button>}
            <p className="text-sm text-subtext mt-5 leading-relaxed">{p.description}</p>

            <PinCheck />

            <div className="mt-5 grid gap-2.5 text-sm" data-testid="pdp-assurances">
              <p className="flex items-center gap-2.5"><Package className="w-5 h-5" strokeWidth={1.5} /> Dispatched in 1–2 working days{settings?.integrations?.shipping_provider === "shiprocket" ? " · live courier tracking on every order" : " from our test shipping configuration"}</p>
              <p className="flex items-center gap-2.5"><RotateCcw className="w-5 h-5" strokeWidth={1.5} /> Easy returns within 7 days — see our <Link to="/policies/returns" className="underline underline-offset-2">return policy</Link></p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 mt-16" data-testid="product-details">
          <Reveal>
            <section aria-label="Full specifications">
              <h2 className="font-display text-3xl">Full specifications</h2>
              <dl className="mt-4 rounded-2xl overflow-hidden [&>div:nth-child(odd)]:bg-surface">
                {Object.entries(p.specs).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-6 py-3.5 px-4 text-sm">
                    <dt>{k}</dt><dd className="text-subtext text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              {p.included.length > 0 && (
                <>
                  <h3 className="font-display text-2xl mt-8">What's included</h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {p.included.map((i2, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-forest shrink-0 mt-0.5" /> {i2}</li>)}
                  </ul>
                </>
              )}
              {p.care.length > 0 && (
                <>
                  <h3 className="font-display text-2xl mt-8">Care & cleaning</h3>
                  <ul className="mt-3 space-y-2 text-sm">
                    {p.care.map((c, i) => <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-forest shrink-0 mt-0.5" /> {c}</li>)}
                  </ul>
                </>
              )}
            </section>
          </Reveal>
          <Reveal delay={0.08}>
            <section aria-label="Frequently asked questions">
              <h2 className="font-display text-3xl">Good to know</h2>
              <div className="mt-4 space-y-3">
                {p.faqs.map((f, i) => (
                  <div key={i} className="bg-white rounded-2xl card-outline">
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i} data-testid={`faq-toggle-${i}`}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-sm font-semibold text-left">
                      {f.q} <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                    </button>
                    {openFaq === i && <p className="px-5 pb-4 text-sm text-charcoal/75 leading-relaxed">{f.a}</p>}
                  </div>
                ))}
                {p.faqs.length === 0 && <p className="text-sm text-charcoal/60">Questions? <Link to="/contact" className="underline font-semibold">Write to us</Link> — we reply within two working days.</p>}
              </div>

              <h3 className="font-display text-2xl mt-10" data-testid="reviews-heading">Reviews {isDemoReview && <span className="text-xs font-normal text-charcoal/50">(sample reviews for demonstration)</span>}</h3>
              <ul className="mt-4 space-y-3">
                {p.reviews.map((r) => (
                  <li key={r.id} className="bg-white rounded-2xl card-outline p-4" data-testid={`pdp-review-${r.id}`}>
                    <div className="flex gap-0.5" aria-label={`${r.rating} out of 5`}>{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-3.5 h-3.5" color={n <= r.rating ? "#EF3C23" : "#E5E0D5"} />)}</div>
                    <p className="text-sm font-bold mt-2">{r.title}</p>
                    <p className="text-sm text-charcoal/75 mt-1">{r.body}</p>
                    <p className="text-xs text-charcoal/50 mt-2">{r.name}{r.demo ? " · sample review" : r.verified ? " · verified purchase" : ""}</p>
                  </li>
                ))}
                {p.reviews.length === 0 && <li className="text-sm text-charcoal/60">No reviews yet — be the first after your purchase.</li>}
              </ul>
              {reviewForm.open ? (
                reviewForm.done ? <p role="status" className="mt-4 text-sm bg-sage/40 rounded-2xl p-4 font-semibold">Thank you! Your review will appear once it's approved.</p> : (
                  <form className="mt-4 bg-white rounded-2xl card-outline p-4 space-y-3" onSubmit={async (e) => {
                    e.preventDefault();
                    try { await api.post("/reviews", { product_slug: p.slug, name: reviewForm.name, rating: reviewForm.rating, title: reviewForm.title, body: reviewForm.body }); setReviewForm((f) => ({ ...f, done: true })); }
                    catch (err) { setReviewForm((f) => ({ ...f, error: "Please check your details and try again." })); }
                  }} data-testid="review-form">
                    <p className="font-semibold text-sm">Write a review</p>
                    <input required value={reviewForm.name} onChange={(e) => setReviewForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full border border-charcoal/15 rounded-full px-4 py-2.5 text-sm" data-testid="review-name-input" />
                    <select value={reviewForm.rating} onChange={(e) => setReviewForm((f) => ({ ...f, rating: +e.target.value }))} className="border border-charcoal/15 rounded-full px-4 py-2.5 text-sm" aria-label="Rating" data-testid="review-rating-select">
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
                    </select>
                    <input required value={reviewForm.title} onChange={(e) => setReviewForm((f) => ({ ...f, title: e.target.value }))} placeholder="Review title" className="w-full border border-charcoal/15 rounded-full px-4 py-2.5 text-sm" data-testid="review-title-input" />
                    <textarea required value={reviewForm.body} onChange={(e) => setReviewForm((f) => ({ ...f, body: e.target.value }))} placeholder="What did you and your little one think?" rows={3} className="w-full border border-charcoal/15 rounded-2xl px-4 py-2.5 text-sm" data-testid="review-body-input" />
                    {reviewForm.error && <p role="alert" className="text-xs text-coral">{reviewForm.error}</p>}
                    <button type="submit" className="bg-forest text-white font-semibold text-sm px-6 py-2.5 rounded-full" data-testid="review-submit">Submit review</button>
                  </form>
                )
              ) : <button onClick={() => setReviewForm((f) => ({ ...f, open: true }))} className="mt-4 text-sm font-semibold underline underline-offset-4" data-testid="open-review-form">Write a review</button>}
            </section>
          </Reveal>
        </div>

        {p.related.length > 0 && (
          <section className="mt-16" data-testid="related-products">
            <h2 className="font-display text-3xl">Goes well with</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {p.related.map((r) => <ProductCard key={r.slug} p={r} />)}
            </div>
          </section>
        )}

        {recent.length > 1 && (
          <section className="mt-16" data-testid="recently-viewed">
            <h2 className="font-display text-3xl">Recently viewed</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {recent.filter((r) => r.slug !== p.slug).slice(0, 4).map((r) => (
                <Link key={r.slug} to={`/products/${r.slug}`} className="bg-white rounded-3xl card-outline overflow-hidden group" data-testid={`recent-${r.slug}`}>
                  <div className="aspect-square overflow-hidden"><img src={r.image} alt={r.name} width="300" height="300" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-3">
                    <p className="text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-sm font-bold mt-0.5">{formatINR(r.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {stock > 0 && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-cream/95 backdrop-blur border-t border-charcoal/10 px-4 py-3 flex items-center gap-3 safe-bottom" data-testid="mobile-purchase-bar">
          <div className="min-w-0">
            <p className="text-[11px] text-charcoal/60 truncate">{variant?.name}</p>
            <p className="font-body text-lg leading-none" data-testid="sticky-bar-price">{formatINR(vPrice * qty)}</p>
          </div>
          <button onClick={() => add()} data-testid="sticky-add-to-cart" className="ml-auto btn-primary h-11 px-6">Add to Cart</button>
        </div>
      )}
    </div>
  );
}
